import React, { useEffect, useState } from "react";
import api, { getApiErrorMessage } from "../../api";
import Dialog from "../common/Dialog";
import FormField, {
  TextInput,
  TextArea,
  SelectInput,
} from "../common/FormField";
import ProfileEditor from "./ProfileEditor";
import useProtectedClose from "../../hooks/useProtectedClose";
import "../../styles/classifier.css";

const OUTCOMES = {
  confirmed: "Подтверждено",
  nonconformity: "Несоответствие",
  insufficient: "Недостаточно данных",
  not_applicable: "Обоснованно неприменимо",
};
const emptyExtension = {
  title: "",
  process_id: "",
  requirement: "",
  actions: "",
  criterion: "",
  evidence: "",
  severity: "major",
  propose: false,
};

export default function AuditClassifier({
  auditId,
  refresh = 0,
  canEdit,
  locked,
  pending,
  onChanged,
}) {
  const [current, setCurrent] = useState(null),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [modal, setModal] = useState("");
  const [revisions, setRevisions] = useState([]),
    [rid, setRid] = useState(""),
    [profile, setProfile] = useState({}),
    [refs, setRefs] = useState({ choices: {}, paths: {} }),
    [companies, setCompanies] = useState([]),
    [preview, setPreview] = useState(null);
  const [reason, setReason] = useState(""),
    [extension, setExtension] = useState(emptyExtension),
    [decision, setDecision] = useState({ key: "", outcome: "confirmed" }),
    [dirty, setDirty] = useState(false);
  const close = useProtectedClose(
    dirty,
    () => {
      setModal("");
      setDirty(false);
      setError("");
    },
    busy,
  );
  const load = async () => {
    try {
      setCurrent((await api.get(`/classifier/audits/${auditId}`)).data);
    } catch (e) {
      if (e.response?.status !== 404) setError(getApiErrorMessage(e));
    }
  };
  useEffect(() => { setCurrent(null); setError(""); }, [auditId]);
  useEffect(() => {
    let active = true;
    api
      .get(`/classifier/audits/${auditId}`)
      .then((r) => {
        if (active) setCurrent(r.data);
      })
      .catch((e) => {
        if (active && e.response?.status !== 404)
          setError(getApiErrorMessage(e));
      });
    return () => {
      active = false;
    };
  }, [auditId, refresh]);
  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const open = (kind) =>
    run(async () => {
      setReason("");
      setDirty(false);
      setPreview(null);
      setExtension({ ...emptyExtension });
      setDecision({ key: "", outcome: "confirmed" });
      setRid(String(current.snapshot.revision.id));
      setProfile(current.snapshot.assembly.input_snapshot);
      const [r, c, f] = await Promise.all([
        api.get("/classifier/revisions"),
        api.get("/companies/"),
        api.get(
          `/classifier/revisions/${current.snapshot.revision.id}/references`,
        ),
      ]);
      setRevisions(r.data.filter((v) => v.state === "published"));
      setCompanies(Array.isArray(c.data) ? c.data : c.data.items || []);
      setRefs(f.data);
      setModal(kind);
    });
  const finish = async () => {
    setModal("");
    setDirty(false);
    await load();
    await onChanged();
  };
  const editable = canEdit && !locked && !pending;
  if (!current)
    return error ? (
      <p role="alert" className="classifier-error">
        {error}
      </p>
    ) : null;
  const snap = current.snapshot,
    review = (snap.review || []).filter((r) => r.state === "requires_review");
  return (
    <section className="classifier-page" style={{ marginBottom: 16 }}>
      <details className="classifier-disclosure">
        <summary>
          Как сформирован чек-лист · {snap.revision.version}
          <span>
            Зафиксированный снимок · {snap.assembly.items.length} проверок ·
            локальных дополнений: {(snap.local_extensions || []).length}
            {review.length ? ` · пересмотреть: ${review.length}` : ""}
          </span>
        </summary>
        <div className="classifier-stack">
          <p className="classifier-notice">
            Новая публикация библиотеки не меняет этот аудит. Ответы, файлы и
            прежние критерии сохраняются. Решение аудитора хранится отдельно от
            рекомендации; замечания оформляются в обычном разделе аудита.
          </p>
          {error && !modal && (
            <p role="alert" className="classifier-error">
              {error}
            </p>
          )}
          {pending && (
            <p className="classifier-notice">
              Сначала сохраните ответы и завершите загрузку файлов.
            </p>
          )}
          <div className="classifier-actions">
            <button
              disabled={!editable || busy}
              className="ap-button ap-button--secondary"
              onClick={() => open("update")}
            >
              Уточнить профиль и сравнить состав
            </button>
            <button
              disabled={!editable || busy}
              className="ap-button ap-button--secondary"
              onClick={() => open("extension")}
            >
              Дополнительная проверка
            </button>
            <button
              disabled={!editable || busy}
              className="ap-button ap-button--secondary"
              onClick={() => open("decision")}
            >
              Зафиксировать решение аудитора
            </button>
          </div>
          {!!review.length && (
            <details className="classifier-disclosure" open>
              <summary>
                Прежние ответы требуют пересмотра ({review.length})
              </summary>
              <div className="classifier-stack">
                {review.map((r, i) => (
                  <article key={i} className="classifier-panel">
                    <strong>{r.requirement_id}</strong>
                    <p>{r.reason}</p>
                    {current.previous_answers
                      .find((a) => a.key === r.previous.key)
                      ?.answers.map((a, j) => (
                        <p key={j}>
                          Прежний ответ:{" "}
                          {String(
                            a.value_text ??
                              a.value_number ??
                              a.value_bool ??
                              "Не заполнен",
                          )}{" "}
                          · {a.comment_text}
                        </p>
                      ))}
                    <button
                      disabled={!editable || busy}
                      className="ap-button ap-button--secondary"
                      onClick={() => {
                        setDecision({
                          key: r.previous.key,
                          outcome: "confirmed",
                        });
                        setReason("");
                        setModal("review");
                      }}
                    >
                      Подтвердить пересмотр
                    </button>
                  </article>
                ))}
              </div>
            </details>
          )}
          <details className="classifier-disclosure">
            <summary>Локальные дополнения и решения</summary>
            <div>
              {(snap.local_extensions || []).map((e) => (
                <p key={e.key}>
                  {e.content.title} · {e.reason}
                  {e.content.propose ? " · предложено в библиотеку" : ""}
                </p>
              ))}
              {(snap.auditor_decisions || []).map((d, i) => (
                <p key={i}>
                  {d.key} · {OUTCOMES[d.outcome]} · {d.reason}
                </p>
              ))}
            </div>
          </details>
        </div>
      </details>
      {modal && (
        <Dialog
          label={
            modal === "update"
              ? "Обновление состава аудита"
              : modal === "extension"
                ? "Дополнительная проверка"
                : "Решение аудитора"
          }
          size="xl"
          className="classifier-dialog"
          onClose={close}
          closeDisabled={busy}
        >
          <div className="classifier-dialog-header">
            <h2 data-dialog-heading tabIndex={-1}>
              {modal === "update"
                ? "Сначала сравните состав"
                : modal === "extension"
                  ? "Проверка только для этого аудита"
                  : "Обоснованное решение аудитора"}
            </h2>
            <button
              disabled={busy}
              className="ap-button ap-button--secondary"
              onClick={close}
            >
              Закрыть
            </button>
          </div>
          <div className="classifier-stack">
            {error && (
              <p role="alert" className="classifier-error">
                {error}
              </p>
            )}
            {modal === "update" && (
              <>
                <FormField label="Опубликованная редакция">
                  <SelectInput
                    value={rid}
                    onChange={(e) => {
                      const id = e.target.value;
                      setRid(id);
                      setPreview(null);
                      setDirty(true);
                      run(async () =>
                        setRefs(
                          (
                            await api.get(
                              `/classifier/revisions/${id}/references`,
                            )
                          ).data,
                        ),
                      );
                    }}
                  >
                    {revisions.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.version} · {r.title}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
                <ProfileEditor
                  value={profile}
                  onChange={(v) => {
                    setProfile(v);
                    setPreview(null);
                    setDirty(true);
                  }}
                  refs={refs}
                  companies={companies}
                />
                <button
                  disabled={busy}
                  className="ap-button ap-button--secondary"
                  onClick={() =>
                    run(async () =>
                      setPreview(
                        (
                          await api.post(
                            `/classifier/audits/${auditId}/preview`,
                            { revision_id: Number(rid), profile },
                          )
                        ).data,
                      ),
                    )
                  }
                >
                  Показать изменения
                </button>
                {preview && (
                  <>
                    <p className="classifier-notice">
                      Добавится {preview.comparison.added.length}, уйдёт в
                      историю {preview.comparison.removed.length}, изменится{" "}
                      {preview.comparison.changed.length}. Уточнений:{" "}
                      {preview.assembly.questions.length}. Старые ответы не
                      переносятся на изменённый критерий автоматически.
                    </p>
                    {[
                      ["added", "Добавить"],
                      ["removed", "В историю"],
                      ["changed", "Пересмотреть"],
                    ].map(([k, label]) => (
                      <details key={k} className="classifier-disclosure">
                        <summary>
                          {label} ({preview.comparison[k].length})
                        </summary>
                        <div>
                          {preview.comparison[k].map((i) => (
                            <p key={(i.after || i).key}>
                              {(i.after || i).requirement_id} ·{" "}
                              {(i.after || i).requirement.title}
                            </p>
                          ))}
                        </div>
                      </details>
                    ))}
                  </>
                )}
              </>
            )}
            {modal === "extension" && (
              <>
                <FormField label="Процесс" required>
                  <SelectInput
                    value={extension.process_id}
                    onChange={(e) => {
                      setDirty(true);
                      setExtension({
                        ...extension,
                        process_id: e.target.value,
                      });
                    }}
                  >
                    <option value="">Выберите процесс</option>
                    {(refs.choices.process || []).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
                {[
                  ["title", "Название"],
                  ["requirement", "Что проверяем"],
                  ["actions", "Действия аудитора"],
                  ["criterion", "Критерий"],
                  ["evidence", "Доказательства"],
                ].map(([k, label]) => (
                  <FormField key={k} label={label} required>
                    <TextArea
                      value={extension[k]}
                      onChange={(e) => {
                        setDirty(true);
                        setExtension({ ...extension, [k]: e.target.value });
                      }}
                    />
                  </FormField>
                ))}
                <label className="classifier-inline">
                  <input
                    type="checkbox"
                    checked={extension.propose}
                    onChange={(e) =>
                      setExtension({ ...extension, propose: e.target.checked })
                    }
                  />
                  Предложить включить в следующую редакцию библиотеки (не
                  публиковать автоматически)
                </label>
              </>
            )}
            {modal === "decision" && (
              <>
                <FormField label="Проверка">
                  <SelectInput
                    value={decision.key}
                    onChange={(e) => {
                      setDirty(true);
                      setDecision({ ...decision, key: e.target.value });
                    }}
                  >
                    <option value="">Выберите проверку</option>
                    {snap.assembly.items.map((i) => (
                      <option key={i.key} value={i.key}>
                        {i.requirement.title} · {i.context.site_id}
                      </option>
                    ))}
                    {(snap.local_extensions || []).map((i) => (
                      <option key={i.key} value={i.key}>
                        {i.content.title}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
                <FormField label="Решение">
                  <SelectInput
                    value={decision.outcome}
                    onChange={(e) =>
                      setDecision({ ...decision, outcome: e.target.value })
                    }
                  >
                    {Object.entries(OUTCOMES).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </SelectInput>
                </FormField>
              </>
            )}
            <FormField label="Обоснование" required>
              <TextArea
                value={reason}
                onChange={(e) => {
                  setDirty(true);
                  setReason(e.target.value);
                }}
              />
            </FormField>
            <div className="classifier-dialog-footer">
              <button
                className="ap-button ap-button--primary"
                disabled={
                  busy ||
                  !editable ||
                  !reason.trim() ||
                  (modal === "update" && !preview) ||
                  (modal === "decision" && !decision.key) ||
                  (modal === "extension" &&
                    [
                      "title",
                      "process_id",
                      "requirement",
                      "actions",
                      "criterion",
                      "evidence",
                    ].some((k) => !extension[k].trim()))
                }
                onClick={() =>
                  run(async () => {
                    if (modal === "update")
                      await api.post(`/classifier/audits/${auditId}/update`, {
                        revision_id: Number(rid),
                        profile,
                        preview_hash: preview.hash,
                        reason,
                        request_key: crypto.randomUUID(),
                      });
                    else
                      await api.post(
                        `/classifier/audits/${auditId}/${modal === "extension" ? "extensions" : modal === "review" ? "review" : "decisions"}`,
                        {
                          snapshot_id: current.id,
                          reason,
                          ...(modal === "extension"
                            ? extension
                            : modal === "review"
                              ? { key: decision.key }
                              : decision),
                        },
                      );
                    await finish();
                  })
                }
              >
                {modal === "update"
                  ? "Применить показанные изменения"
                  : "Сохранить"}
              </button>
              <button
                disabled={busy}
                className="ap-button ap-button--secondary"
                onClick={close}
              >
                Отмена
              </button>
            </div>
          </div>
        </Dialog>
      )}
    </section>
  );
}
